// Module 11's one Pinia store — the credit balance and the shared Upgrade modal (contract §9).
//
// A SETUP store, not an options store, so the `accountId` watch below can live inside the store's
// own effect scope. v3 kept that watch in `components/AiCreditSlot.vue:108-116`, which meant only
// the topbar slot refreshed on an account switch — every other consumer (locked cards, panels,
// settings) kept the previous tenant's numbers until something else happened to refetch.
//
// Two legacy invariants are deliberate and preserved verbatim (see ORP-18684):
//   · `fetchBalance` is SILENT on failure — `error=true`, prior balance kept, never a toast. This
//     is an ambient surface; a failed poll must not interrupt the user.
//   · A free trial is UNCAPPED, so `isLocked` stays false while a trial is active even at zero
//     remaining. Contract §9's `isLocked = locked || remaining <= 0` omits that clause; following
//     it literally would lock every trial user out of the features the trial exists to sell.
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { BillingApi } from '../api/BillingApi'
import { CreditApi } from '../api/CreditApi'
import type { BillingPackageDto } from '../models/billing.types'
import type { PlansConfig } from '../models/credit.config.types'
import type {
  BalancePayload,
  CreativePreview,
  CreativePreviewConfigParams,
  CreativePreviewCountsParams,
  HistoryPayload,
  RelabelPreview,
} from '../models/credit.types'
import { isBalanceFresh, isPricingCatalogFresh } from '../service/creditFreshness'
import {
  deriveSlotState,
  featurePresentation,
  formatPeriod,
  formatResetDate,
  isOnTrial,
  type CreditSlotState,
  type FeaturePresentation,
} from '../service/creditPresentation'

import { useAccount } from '@/features/account'

export interface SpendSegment {
  key: string
  presentation: FeaturePresentation
  spent: number
}

/** The three features that bill credits, in the order the panel's segmented bar renders them. */
const FEATURE_SPEND_ORDER = ['orpheus.chat', 'causal.impact', 'creative_ai.label']

export const useCreditStore = defineStore(
  'creditStore',
  () => {
    const api = new CreditApi()
    const billingApi = new BillingApi()
    const { accountId } = useAccount()

    const balance = ref<BalancePayload | null>(null)
    const balanceAccountId = ref<number | null>(null) // account `balance` was fetched for
    const balanceFetchedAt = ref<number | null>(null)
    const plansConfig = ref<PlansConfig | null>(null)
    const plansConfigLoadingForId = ref<number | null>(null)
    const plansConfigLoading = computed(() => plansConfigLoadingForId.value !== null)
    const pricingPackages = ref<BillingPackageDto[]>([])
    const pricingPackagesFetchedAt = ref<number | null>(null)
    const pricingPackagesLoading = ref(false)
    const pricingPackagesLoaded = ref(false)
    const pricingPackagesError = ref(false)
    const loading = ref(false)
    const loaded = ref(false)
    const error = ref(false)
    /** Shared Upgrade modal — opened from the slot panel and every locked card. */
    const upgradeOpen = ref(false)
    /** Full plan-selection takeover — requested by auth/account through module 11's public seam. */
    const planSelectionOpen = ref(false)

    const hasBalance = computed(() => balance.value !== null)
    const slotState = computed<CreditSlotState>(() => deriveSlotState(balance.value))
    const isTrial = computed(() => isOnTrial(balance.value))
    const isLocked = computed(
      () =>
        balance.value !== null &&
        !isOnTrial(balance.value) &&
        (balance.value.locked || balance.value.remaining <= 0),
    )
    const isDepleted = computed(() => slotState.value === 'depleted')
    const isLow = computed(() => slotState.value === 'low')

    const remaining = computed(() => balance.value?.remaining ?? 0)
    const total = computed(() => balance.value?.total ?? 0)
    const planCredits = computed(() => balance.value?.plan_credits ?? 0)
    const plan = computed(() => balance.value?.plan ?? null)
    const buckets = computed(() => balance.value?.buckets ?? { trial: 0, addon: 0, base: 0 })

    const addon = computed(
      () => balance.value?.addon ?? { total: 0, remaining: 0, expires_at: null, lots: [] },
    )
    const addonLots = computed(() => balance.value?.addon?.lots ?? [])
    const addonRemaining = computed(
      () => balance.value?.addon?.remaining ?? balance.value?.buckets?.addon ?? 0,
    )
    /** Soonest-expiring active add-on lot — NOT the cycle reset date, and null when no add-on. */
    const addonExpiresAt = computed(() => balance.value?.addon?.expires_at ?? null)

    const resetDateLabel = computed(() => formatResetDate(balance.value?.reset_date))
    const startDate = computed(() => balance.value?.start_date ?? null)
    /** "Jun 1 – Jul 1, 2026" — the current cycle window (period start → reset). */
    const periodLabel = computed(() =>
      formatPeriod(balance.value?.start_date, balance.value?.reset_date),
    )
    const trialDaysRemaining = computed(() => balance.value?.trial_days_remaining ?? null)
    const trialExpiresAt = computed(() => balance.value?.trial_expires_at ?? null)

    const spentTotal = computed(() =>
      Object.values(balance.value?.spend_by_feature ?? {}).reduce(
        (acc, n) => acc + (Number(n) || 0),
        0,
      ),
    )

    /** Per-feature spend, decorated and ordered: the three known features first, then any unknown
     *  key the backend adds. Unknown keys are never dropped — the map is open (contract §5). */
    const spendBreakdown = computed<SpendSegment[]>(() => {
      const map = balance.value?.spend_by_feature ?? {}
      const keys = [
        ...FEATURE_SPEND_ORDER.filter((key) => key in map),
        ...Object.keys(map).filter((key) => !FEATURE_SPEND_ORDER.includes(key)),
      ]
      return keys.map((key) => ({
        key,
        presentation: featurePresentation(key),
        spent: Number(map[key]) || 0,
      }))
    })

    function reset(): void {
      balance.value = null
      balanceAccountId.value = null
      balanceFetchedAt.value = null
      plansConfig.value = null
      plansConfigLoadingForId.value = null
      loading.value = false
      loaded.value = false
      error.value = false
      upgradeOpen.value = false
      planSelectionOpen.value = false
    }

    /** User-visible plan/add-on catalog. Unavailable config simply removes those optional blocks. */
    async function fetchPlansConfig(force = false): Promise<void> {
      const id = accountId.value
      if (id === undefined) {
        plansConfig.value = null
        return
      }
      if (plansConfigLoadingForId.value === id || (plansConfig.value && !force)) return

      plansConfigLoadingForId.value = id
      try {
        const payload = await api.getPlansConfig(id)
        if (accountId.value === id) plansConfig.value = payload
      } catch {
        // Optional ambient catalog — the balance and history remain usable without it.
        if (accountId.value === id) plansConfig.value = null
      } finally {
        if (plansConfigLoadingForId.value === id) plansConfigLoadingForId.value = null
      }
    }

    /** Public subscription catalog; missing/failed data never falls back to invented plan state. */
    async function fetchPricingPackages(force = false): Promise<void> {
      if (pricingPackagesLoading.value) return
      const cached = isPricingCatalogFresh(
        pricingPackagesLoaded.value,
        pricingPackagesFetchedAt.value,
      )
      if (!force && cached) return

      pricingPackagesLoading.value = true
      pricingPackagesError.value = false
      try {
        pricingPackages.value = await billingApi.listPricingPackages()
        pricingPackagesLoaded.value = true
        pricingPackagesFetchedAt.value = Date.now()
      } catch {
        pricingPackages.value = []
        pricingPackagesLoaded.value = false
        pricingPackagesFetchedAt.value = null
        pricingPackagesError.value = true
      } finally {
        pricingPackagesLoading.value = false
      }
    }

    /** Account-aware history read for the feature's composable; null keeps failures silent. */
    async function fetchHistoryPage(params: {
      limit: number
      offset: number
    }): Promise<HistoryPayload | null> {
      const id = accountId.value
      if (id === undefined) return null
      try {
        return await api.getHistory(id, params)
      } catch {
        return null
      }
    }

    // A `force` call landing while another fetch is already in flight must not be silently
    // swallowed — two billable actions completing close together (e.g. an Orpheus turn and a
    // causal run both finishing within the same request window) would otherwise drop one side's
    // refresh, leaving the balance stale until some unrelated future fetch happens to run. Instead
    // of returning early, a force call joins the in-flight request and then re-fetches after it —
    // an unforced call joins and stops there, matching the existing load-once semantics.
    let inFlight: Promise<void> | null = null

    /** Fetch the balance for the selected account. Silent on failure — see the header. Short-TTL
     *  cache (contract 11 §7 amended 2026-09-09): still refetches on every account switch/login. */
    async function fetchBalance(force = false): Promise<void> {
      const id = accountId.value
      if (id === undefined) {
        reset()
        return
      }
      const fresh = isBalanceFresh(loaded.value, balanceAccountId.value, id, balanceFetchedAt.value)
      if (inFlight) {
        await inFlight
        if (!force) return
      } else if (!force && fresh) {
        return
      }

      loading.value = true
      error.value = false
      inFlight = (async () => {
        try {
          balance.value = await api.getBalance(id)
          loaded.value = true
          balanceAccountId.value = id
          balanceFetchedAt.value = Date.now()
        } catch {
          // Ambient surface — never alert. Leave any prior balance in place.
          error.value = true
        } finally {
          loading.value = false
        }
      })()
      try {
        await inFlight
      } finally {
        inFlight = null
      }
    }

    /** Force a refresh — call after any billable AI action (chat send, run, etc.). */
    function refresh(): Promise<void> {
      return fetchBalance(true)
    }

    /**
     * The store-owned Creative AI preview boundary. Components and composables stay above the API
     * layer and call this action, so tenant selection and the HTTP owner cannot drift apart.
     */
    async function previewCreativeConfig(
      params: CreativePreviewConfigParams,
    ): Promise<CreativePreview | null> {
      const id = accountId.value
      return id === undefined ? null : api.getCreativePreviewConfig(id, params)
    }

    async function previewCreativeCounts(
      params: CreativePreviewCountsParams,
    ): Promise<CreativePreview | null> {
      const id = accountId.value
      return id === undefined ? null : api.getCreativePreview(id, params)
    }

    /** Module 23's read-only relabel estimate. The store keeps account selection beside the API
     *  call; Creative AI supplies only its workspace and scope through the public composable. */
    async function previewRelabel(params: {
      scope: 'image' | 'video'
      workspace_id: number
    }): Promise<RelabelPreview | null> {
      const id = accountId.value
      return id === undefined ? null : api.getRelabelPreview(id, params)
    }

    /** Hydrate straight from a `BalancePayload` — e.g. the `details` of a 402 CREDIT_DEPLETED, so
     *  lock states reflect server truth with no extra call. Marks the cache fresh too, so a
     *  mounting consumer's unforced `fetchBalance()` no-ops instead of overwriting this payload. */
    function setBalance(payload: BalancePayload | null): void {
      if (!payload) return
      balance.value = payload
      loaded.value = true
      error.value = false
      balanceAccountId.value = accountId.value ?? null
      balanceFetchedAt.value = Date.now()
    }

    /** Apply an Orpheus chat response's `credits` block — a partial update so the topbar reflects
     *  the new total without a refetch. No-ops when no balance is loaded or the block is absent
     *  (sysadmin/exempt calls omit it). */
    function applyChatCredits(credits?: { remaining: number; locked: boolean } | null): void {
      if (!credits || !balance.value) return
      balance.value = { ...balance.value, remaining: credits.remaining, locked: credits.locked }
    }

    function openUpgrade(): void {
      upgradeOpen.value = true
    }
    function closeUpgrade(): void {
      upgradeOpen.value = false
    }
    function requestPlanSelection(): void {
      planSelectionOpen.value = true
    }
    function resetPlanSelection(): void {
      planSelectionOpen.value = false
    }

    // Account switch → drop the previous tenant's balance and refetch for the new one. `undefined`
    // covers logout too, so no separate session-reset registration is needed: the watch is the one
    // place that decides what "no account" means.
    watch(accountId, (next) => {
      reset()
      if (next !== undefined) void fetchBalance(true)
    })

    return {
      balance,
      balanceAccountId,
      balanceFetchedAt,
      plansConfig,
      plansConfigLoading,
      pricingPackages,
      pricingPackagesFetchedAt,
      pricingPackagesLoading,
      pricingPackagesLoaded,
      pricingPackagesError,
      loading,
      loaded,
      error,
      upgradeOpen,
      planSelectionOpen,
      hasBalance,
      slotState,
      isTrial,
      isLocked,
      isDepleted,
      isLow,
      remaining,
      total,
      planCredits,
      plan,
      buckets,
      addon,
      addonLots,
      addonRemaining,
      addonExpiresAt,
      resetDateLabel,
      startDate,
      periodLabel,
      trialDaysRemaining,
      trialExpiresAt,
      spentTotal,
      spendBreakdown,
      fetchBalance,
      fetchPlansConfig,
      fetchPricingPackages,
      fetchHistoryPage,
      refresh,
      previewCreativeConfig,
      previewCreativeCounts,
      previewRelabel,
      setBalance,
      applyChatCredits,
      openUpgrade,
      closeUpgrade,
      requestPlanSelection,
      resetPlanSelection,
      reset,
    }
  },
  {
    persist: {
      key: 'creditStore',
      // Short-TTL caches only (contract 11 §7 amended 2026-09-09); `upgradeOpen`/`planSelectionOpen`
      // and plans-config stay unpersisted — session-transient, not caches.
      pick: [
        'balance',
        'balanceAccountId',
        'balanceFetchedAt',
        'loaded',
        'pricingPackages',
        'pricingPackagesFetchedAt',
        'pricingPackagesLoaded',
      ],
    },
  },
)
